import { useMemo, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { img, effPrice } from "./PublicMenu";

export function ProductSheet({ product, menu, onClose }) {
  const { addItem } = useCart();
  const vgroups = menu.variation_groups.filter((g) => product.variation_group_ids?.includes(g.id));
  const agroups = menu.addon_groups.filter((g) => product.addon_group_ids?.includes(g.id));
  const [vars, setVars] = useState(() => Object.fromEntries(vgroups.map((g) => {
    const opts = g.options.filter((o) => o.is_active);
    const d = opts.find((o) => o.is_default) || (g.is_required ? opts[0] : null);
    return [g.id, d?.id || ""];
  })));
  const sizes = product.sizes || [];
  const [sizeId, setSizeId] = useState("");
  const [adds, setAdds] = useState({});
  const [qty, setQty] = useState(1);

  const unit = useMemo(() => {
    let u = effPrice(product);
    const fixed = vgroups.find((g) => g.price_mode === "fixed" && vars[g.id]);
    if (fixed) u = fixed.options.find((o) => o.id === vars[fixed.id])?.price || 0;
    if (sizes.length) u = sizes.find((s) => s.id === sizeId)?.price ?? Math.min(...sizes.map((s) => s.price));
    vgroups.filter((g) => g.price_mode !== "fixed").forEach((g) => { u += g.options.find((o) => o.id === vars[g.id])?.price || 0; });
    agroups.forEach((g) => (adds[g.id] || []).forEach((id) => { u += g.options.find((o) => o.id === id)?.price || 0; }));
    return u;
  }, [vars, adds, sizeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAdd = (g, id) => {
    const cur = adds[g.id] || [];
    if (cur.includes(id)) return setAdds({ ...adds, [g.id]: cur.filter((x) => x !== id) });
    if (g.max_select && cur.length >= g.max_select) return toast.error(`Choose up to ${g.max_select} for ${g.name}`);
    setAdds({ ...adds, [g.id]: [...cur, id] });
  };

  const add = () => {
    if (sizes.length && !sizeId) return toast.error("Please choose a size");
    for (const g of vgroups) if (g.is_required && !vars[g.id]) return toast.error(`Please choose ${g.name}`);
    for (const g of agroups) {
      const n = (adds[g.id] || []).length;
      if (n < (g.min_select || 0)) return toast.error(`Choose at least ${g.min_select} for ${g.name}`);
    }
    const labels = [
      ...(sizeId ? [sizes.find((s) => s.id === sizeId)?.name] : []),
      ...vgroups.filter((g) => vars[g.id]).map((g) => g.options.find((o) => o.id === vars[g.id])?.name),
      ...agroups.flatMap((g) => (adds[g.id] || []).map((id) => g.options.find((o) => o.id === id)?.name)),
    ];
    addItem({
      product_id: product.id, name: product.name, quantity: qty, unit_price: unit, labels, size_id: sizeId,
      variation_selections: vgroups.filter((g) => vars[g.id]).map((g) => ({ group_id: g.id, option_id: vars[g.id] })),
      addon_selections: agroups.filter((g) => (adds[g.id] || []).length).map((g) => ({ group_id: g.id, option_ids: adds[g.id] })),
    });
    toast.success(`${product.name} added`);
    onClose();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto p-0" data-testid="product-sheet">
        {product.image_url && <img src={img(product.image_url)} alt={product.name} className="h-52 w-full object-cover" />}
        <div className="space-y-5 p-5">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">{product.name}</DialogTitle>
            {product.description && <p className="text-sm text-slate-500">{product.description}</p>}
          </DialogHeader>
          {sizes.length > 0 && (
            <div data-testid="size-picker">
              <p className="mb-2 text-sm font-semibold text-slate-800">Size <span className="text-xs font-normal text-rose-500">Required</span></p>
              <div className="space-y-1.5">
                {sizes.map((s) => (
                  <label key={s.id} className={`flex cursor-pointer items-center justify-between rounded-xl border px-3 py-2.5 text-sm transition-colors ${sizeId === s.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`size-option-${s.id}`}>
                    <span className="flex items-center gap-2"><input type="radio" className="accent-emerald-500" checked={sizeId === s.id} onChange={() => setSizeId(s.id)} />{s.name}</span>
                    <span className="text-slate-500">{money(s.price)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {vgroups.map((g) => (
            <div key={g.id}>
              <p className="mb-2 text-sm font-semibold text-slate-800">{g.name} {g.is_required && <span className="text-xs font-normal text-rose-500">Required</span>}</p>
              <div className="space-y-1.5">
                {g.options.filter((o) => o.is_active).map((o) => (
                  <label key={o.id} className={`flex cursor-pointer items-center justify-between rounded-xl border px-3 py-2.5 text-sm transition-colors ${vars[g.id] === o.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`variation-option-${o.id}`}>
                    <span className="flex items-center gap-2">
                      <input type="radio" className="accent-emerald-500" checked={vars[g.id] === o.id} onChange={() => setVars({ ...vars, [g.id]: o.id })} />{o.name}
                    </span>
                    <span className="text-slate-500">{g.price_mode === "fixed" ? money(o.price) : o.price > 0 ? `+${money(o.price)}` : ""}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          {agroups.map((g) => (
            <div key={g.id}>
              <p className="mb-2 text-sm font-semibold text-slate-800">{g.name}
                <span className="ml-2 text-xs font-normal text-slate-400">{g.min_select ? `Min ${g.min_select}` : "Optional"}{g.max_select ? ` · Max ${g.max_select}` : ""}</span>
              </p>
              <div className="space-y-1.5">
                {g.options.filter((o) => o.is_active).map((o) => {
                  const on = (adds[g.id] || []).includes(o.id);
                  return (
                    <label key={o.id} className={`flex cursor-pointer items-center justify-between rounded-xl border px-3 py-2.5 text-sm transition-colors ${on ? "border-emerald-500 bg-emerald-50" : "border-slate-200"}`} data-testid={`addon-option-${o.id}`}>
                      <span className="flex items-center gap-2"><input type="checkbox" className="accent-emerald-500" checked={on} onChange={() => toggleAdd(g, o.id)} />{o.name}</span>
                      <span className="text-slate-500">{o.price > 0 && `+${money(o.price)}`}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-3 pt-2">
            <div className="flex items-center rounded-full border border-slate-200">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5" data-testid="sheet-qty-minus"><Minus className="h-4 w-4" /></button>
              <span className="w-6 text-center font-semibold" data-testid="sheet-qty">{qty}</span>
              <button onClick={() => setQty(Math.min(50, qty + 1))} className="p-2.5" data-testid="sheet-qty-plus"><Plus className="h-4 w-4" /></button>
            </div>
            <Button onClick={add} className="h-11 flex-1 rounded-full bg-emerald-500 text-base hover:bg-emerald-600" data-testid="sheet-add-btn">
              Add · {money(unit * qty)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
